import { ReactElement, useCallback, useRef } from 'react';
import { MATERIAL_VIEWS } from '../../../../constants/search';
import useMaterialCardImpressionTracking from '../../../../hooks/quality-events/use-material-card-impression-tracking';
import CardContext from './contexts/favorites-material-card-context';
import useAddToCartSuccess from './hooks/use-add-to-cart-success';
import useCardVisibility from './hooks/use-card-visibility';
import useFolderRemovalObserver from './hooks/use-folder-removal-observer';
import { FavoritesMaterialCardProps, FavoritesMaterialCardVariationProps } from './types';
import DesktopGridCard from './variations/desktop-grid-card/desktop-grid-card';
import DesktopListCard from './variations/desktop-list-card/desktop-list-card';
import MobileCard from './variations/mobile-card/mobile-card';

export type { FavoritesMaterialCardProps, FavoritesMaterialCardVariationProps };

const FavoritesMaterialCard = (props: FavoritesMaterialCardProps): ReactElement => {
    const { viewMode, item, index, internalPath, onRemove, ...rest } = props;

    const isGridView = viewMode === MATERIAL_VIEWS.GRID;

    const cardRef = useRef<HTMLDivElement>(null);
    const setImpressionNode = useMaterialCardImpressionTracking(item, { internalPath, itemIndex: index });

    const setCardNode = useCallback(
        (node: HTMLDivElement | null) => {
            cardRef.current = node;
            setImpressionNode(node);
        },
        [setImpressionNode]
    );

    const cardVisibility = useCardVisibility();
    const onAddToCartSuccess = useAddToCartSuccess();

    useFolderRemovalObserver(item, onRemove);

    const Card = cardVisibility.isTabletOrMobile ? MobileCard : isGridView ? DesktopGridCard : DesktopListCard;

    return (
        <CardContext.Provider value={{ cardRef, setCardNode, onRemove }}>
            <Card
                item={item}
                index={index}
                internalPath={internalPath}
                shouldShowFolder={cardVisibility.shouldShowFolder}
                shouldShowAddToCart={cardVisibility.shouldShowAddToCart}
                onAddToCartSuccess={onAddToCartSuccess}
                {...rest}
            />
        </CardContext.Provider>
    );
};

export default FavoritesMaterialCard;
